import { useCallback, useEffect, useState } from 'react'
import { CircleHelp, Plus } from 'lucide-react'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import OnboardingTour from '../../../../components/ERP/Onboarding/OnboardingTour'
import Toast from '../../../../components/Toast/Toast'
import { useAuth } from '../../../../contexts/AuthContext'
import { useToast } from '../../../../hooks/useToast'
import { patientService } from '../../../../services/patientApi'
import NutritionPlaceModal from './NutritionPlaceModal'
import NutritionPrescriptionModal from './NutritionPrescriptionModal'
import NutritionPrescriptions from './NutritionPrescriptions'
import NutritionTimeline from './NutritionTimeline'
import NutritionWaterButton from './NutritionWaterButton'
import { todayInputValue } from './nutritionFormat'
import { NUTRITION_ONBOARDING_STEPS, NUTRITION_TOUR_ID } from './nutritionOnboarding'

function NutritionTab({ patientId, patientName, archived, canEdit, canSeePrescriptions, canCreatePrescriptions = false, canRecordMeal = false, canRecordWater = false }) {
  const { user } = useAuth()
  const [replay, setReplay] = useState(0)
  const [date, setDate] = useState(todayInputValue)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [suspending, setSuspending] = useState(null)
  const [suspendReason, setSuspendReason] = useState('')
  const [placing, setPlacing] = useState(null)
  const [placingError, setPlacingError] = useState('')
  const [placingBusy, setPlacingBusy] = useState(false)
  const [goalOpen, setGoalOpen] = useState(false)
  const [goal, setGoal] = useState('1500')
  const { toast, showToast, hideToast } = useToast()

  const load = useCallback(async () => {
    setError('')
    try {
      const response = await patientService.nutritionDay(patientId, date)
      setData(response.data)
      setGoal(String(response.data?.water_goal_ml ?? 1500))
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível carregar a alimentação')
    }
  }, [patientId, date])

  useEffect(() => {
    setData(null)
    load()
  }, [load])

  useEffect(() => {
    const onWater = () => load()
    window.addEventListener('wfmed-nutrition-water', onWater)
    return () => window.removeEventListener('wfmed-nutrition-water', onWater)
  }, [load])

  const refresh = async (message) => {
    await load()
    if (message) showToast(message, 'success')
  }

  const run = async (action, message) => {
    setError('')
    try {
      await action()
      await refresh(message)
    } catch (err) {
      const text = err.response?.data?.message || 'Não foi possível atualizar a alimentação'
      setError(text)
      showToast(text, 'error')
    }
  }

  const closeSuspend = () => {
    setSuspending(null)
    setSuspendReason('')
  }

  const waterMet = data && data.water_consumed_ml >= data.water_goal_ml && data.water_goal_ml > 0
  const alerts = data?.stock_alerts || 0

  return (
    <div className="med-panel">
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={hideToast} />
      <div className="therapy-toolbar">
        <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={() => setReplay((current) => current + 1)}>
          <CircleHelp size={14} /> Como usar
        </button>
      </div>
      <div className="patients-metrics patients-metrics--3" data-onboarding="nutrition-summary">
        <article className="patients-metric">
          <div className="patients-metric-label nut-water-head">
            <span>Água do dia</span>
            {canEdit && data && (
              goalOpen ? (
                <form
                  className="nut-goal-form nut-goal-form--head"
                  onSubmit={(event) => {
                    event.preventDefault()
                    const value = Number(goal)
                    if (!Number.isInteger(value) || value < 0) return
                    setGoalOpen(false)
                    run(() => patientService.updateNutritionGoal(patientId, value), 'Meta de água atualizada.')
                  }}
                >
                  <input type="number" min="0" max="10000" step="1" value={goal} aria-label="Meta de água em ml" onChange={(event) => setGoal(event.target.value)} />
                  <button type="submit" className="patients-btn patients-btn--primary patients-btn--compact">Salvar</button>
                </form>
              ) : (
                <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={() => setGoalOpen(true)}>
                  Ajustar meta
                </button>
              )
            )}
          </div>
          <p className={`patients-metric-value nut-metric-value${waterMet ? ' tone-green' : ''}`}>
            {data ? `${data.water_consumed_ml} / ${data.water_goal_ml} ml` : '—'}
          </p>
        </article>
        <article className="patients-metric">
          <div className="patients-metric-label">Aceite médio do dia</div>
          <p className="patients-metric-value">{data?.acceptance_average == null ? '—' : `${data.acceptance_average}%`}</p>
          <span>{data?.acceptance_average == null ? 'Nenhuma oferta registrada' : 'Ofertas já lançadas'}</span>
        </article>
        <article className="patients-metric">
          <div className={`patients-metric-label${alerts ? ' tone-amber' : ' tone-green'}`}>Alertas de estoque</div>
          <p className={`patients-metric-value${alerts ? ' tone-amber' : ' tone-green'}`}>{data ? alerts : '—'}</p>
          <span>{alerts ? 'Fórmula ou insumo acabando' : 'Estoque dentro da meta'}</span>
        </article>
      </div>
      {(canRecordWater || (canEdit && canCreatePrescriptions)) && (
        <div className="med-toolbar nut-toolbar">
          <div className="nut-toolbar-actions">
            {canRecordWater && (
              <NutritionWaterButton
                patientId={patientId}
                patientName={patientName}
                date={date}
                onAdded={() => refresh('Água livre lançada.')}
              />
            )}
            {canEdit && canCreatePrescriptions && (
              <button type="button" className="patients-btn patients-btn--primary" onClick={() => { setEditing(null); setOpen(true) }}>
                <Plus size={14} /> Nova refeição
              </button>
            )}
          </div>
        </div>
      )}
      {error && <p className="patients-alert-strip">{error}</p>}
      <div className="med-board nut-board">
        <NutritionTimeline
          date={date}
          items={data?.timeline || []}
          loading={!data && !error}
          failed={Boolean(error)}
          canEdit={canRecordMeal && Boolean(data)}
          canRemoveWater={canRecordWater && Boolean(data)}
          canPlace={canRecordMeal && canSeePrescriptions && Boolean(data)}
          onPlace={(plateId) => {
            const plate = (data?.active || []).find((item) => String(item.id) === String(plateId))
            if (!plate) return
            setPlacingError('')
            setPlacing(plate)
          }}
          onDateChange={setDate}
          onRecord={(item, payload) => run(
            () => patientService.recordNutritionSchedule(patientId, item.id, payload),
            payload.outcome === 'recusado' ? 'Recusa registrada.' : 'Aceite registrado.'
          )}
          onDeleteWater={(item) => run(() => patientService.deleteNutritionHydration(patientId, item.hydration_id), 'Lançamento removido.')}
        />
        <div className="med-side">
          {!data && <p className="patients-muted">Carregando alimentação…</p>}
          <NutritionPrescriptions
            active={data?.active || []}
            suspended={data?.suspended || []}
            canSee={canSeePrescriptions}
            canEdit={canCreatePrescriptions && Boolean(data)}
            canStock={canEdit && Boolean(data)}
            onEdit={(item) => { setEditing(item); setOpen(true) }}
            onSuspend={(item) => { setSuspendReason(''); setSuspending(item) }}
            onReload={(item, quantity) => run(() => patientService.reloadNutritionStock(patientId, item.id, quantity), 'Estoque atualizado.')}
          />
        </div>
      </div>
      <ConfirmModal
        isOpen={Boolean(suspending)}
        onClose={closeSuspend}
        onConfirm={() => {
          const item = suspending
          const reason = suspendReason.trim()
          closeSuspend()
          if (!item) return
          run(() => patientService.suspendNutritionPrescription(patientId, item.id, reason), 'Refeição suspensa.')
        }}
        title="Suspender refeição"
        type="warning"
        confirmText="Suspender"
        message={(
          <div className="med-suspend-copy">
            <p>
              Suspender <strong>{suspending?.name}</strong>? As ofertas pendentes deixam de aparecer na linha do tempo.
            </p>
            <label className="med-suspend-field">
              Motivo (opcional)
              <textarea
                value={suspendReason}
                onChange={(event) => setSuspendReason(event.target.value)}
                maxLength={200}
                rows={3}
                placeholder="Ex.: alta, troca de fórmula, intolerância"
              />
            </label>
          </div>
        )}
      />
      <NutritionPlaceModal
        isOpen={Boolean(placing)}
        plate={placing}
        saving={placingBusy}
        error={placingError}
        onClose={() => { if (!placingBusy) setPlacing(null) }}
        onConfirm={async (payload) => {
          if (!placing) return
          setPlacingBusy(true)
          setPlacingError('')
          try {
            await patientService.placeNutritionPrescription(patientId, placing.id, payload)
            setPlacing(null)
            await refresh('Prato colocado na linha do tempo.')
          } catch (err) {
            setPlacingError(err.response?.data?.message || 'Não foi possível agendar o prato')
          } finally {
            setPlacingBusy(false)
          }
        }}
      />
      <NutritionPrescriptionModal
        isOpen={open}
        patientId={patientId}
        prescription={editing}
        onClose={() => setOpen(false)}
        onSaved={() => refresh('Refeição salva.')}
      />
      {archived && <p className="patients-muted">Paciente arquivado. A alimentação fica somente para leitura.</p>}
      <OnboardingTour
        tourId={NUTRITION_TOUR_ID}
        steps={NUTRITION_ONBOARDING_STEPS}
        userId={user?.id}
        ready={Boolean(data)}
        replayToken={replay}
      />
    </div>
  )
}

export default NutritionTab
